/**
 * MKS-341 — the due-date chip on a board card and in the slide-over's header.
 *
 * Renders nothing at all when the ticket has no due date, which is the default
 * for every ticket — "no due date = no change to current behaviour" is the
 * ticket's own acceptance criterion, so this component must be invisible, not
 * merely empty, in that case.
 *
 * The three states come from the ONE ladder in `utils/taskDueDate.ts`; this
 * file only maps them to colour. The colour is never the sole carrier of the
 * meaning: the `title` and the `aria-label` both spell the state out
 * ("Overdue — was due 20 Oct"), so the chip reads the same to a screen reader
 * and to anyone who cannot separate the amber from the red.
 *
 * Palette follows the card's existing Tailwind semantic chips (the amber
 * `External` pill and the red/amber `BlockRelationLine`), not the ms-* tokens
 * — the D-Tokens decision keeps those semantic colours as they are.
 */
import { CalendarIcon } from '@heroicons/react/24/outline';
import { dueDateLabel, formatDueDate, taskDueState, type TaskDueState } from '../../utils/taskDueDate';

const STATE_CLASS: Record<TaskDueState, string> = {
  overdue: 'bg-red-50 text-red-700',
  'due-soon': 'bg-amber-50 text-amber-700',
  // An ordinary future date is information, not a warning — same neutral
  // treatment the card gives any other metadata chip.
  upcoming: 'bg-ms-surface-2 text-ms-text-secondary',
};

export interface TaskDueChipProps {
  /** The ticket's own due date, `YYYY-MM-DD`, or null/undefined when unset. */
  dueDate: string | null | undefined;
  /**
   * Whether the ticket sits in a terminal column. Passed straight through to
   * `taskDueState`, which is what guarantees a Done column never goes red.
   */
  isTerminal?: boolean | undefined;
  /** Injectable clock for tests. */
  now?: Date | undefined;
}

export function TaskDueChip({ dueDate, isTerminal, now }: TaskDueChipProps) {
  const state = taskDueState(dueDate, { isTerminal, now });
  if (state === null || !dueDate) return null;

  const label = dueDateLabel(dueDate, state, now ?? new Date());

  return (
    <span
      data-testid="task-due-chip"
      data-due-state={state}
      title={label}
      aria-label={label}
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium leading-none shrink-0 ${STATE_CLASS[state]}`}
    >
      <CalendarIcon className="h-3 w-3 shrink-0" aria-hidden />
      {formatDueDate(dueDate, now ?? new Date())}
    </span>
  );
}
